/**
 * Category utility functions for building category trees preserving the exact
 * order returned by the API response (which reflects the user's custom order).
 */

/**
 * Builds a hierarchical category tree (roots with nested children)
 * preserving the exact order from the API response without client-side sorting.
 *
 * @param {Array} rawCategories - Array of category objects from API response
 * @returns {Array} - Array of root category objects with nested children in API order
 */
export const buildCategoryHierarchy = (rawCategories) => {
  if (!Array.isArray(rawCategories) || rawCategories.length === 0) return [];

  const categoryMap = new Map();
  const orderedList = [];

  // Register all categories in the map preserving API response order
  rawCategories.forEach((cat) => {
    if (!cat || cat.id == null) return;
    const id = String(cat.id);
    if (!categoryMap.has(id)) {
      const node = { ...cat, children: [] };
      categoryMap.set(id, node);
      orderedList.push(node);
    }
  });

  // Also register any nested parent or child objects if not already present
  rawCategories.forEach((cat) => {
    if (!cat) return;
    if (cat.parent && cat.parent.id != null) {
      const pid = String(cat.parent.id);
      if (!categoryMap.has(pid)) {
        const pnode = { ...cat.parent, children: [] };
        categoryMap.set(pid, pnode);
        orderedList.push(pnode);
      }
    }
    if (Array.isArray(cat.children)) {
      cat.children.forEach((child) => {
        if (!child || child.id == null) return;
        const cid = String(child.id);
        if (!categoryMap.has(cid)) {
          const cnode = { ...child, children: [] };
          categoryMap.set(cid, cnode);
          orderedList.push(cnode);
        }
      });
    }
  });

  // Reconstruct tree hierarchy in the exact API response order
  const rootCategories = [];
  orderedList.forEach((cat) => {
    const parentId =
      cat.parent_id !== null &&
        cat.parent_id !== undefined &&
        cat.parent_id !== ""
        ? String(cat.parent_id)
        : cat.parent?.id != null
          ? String(cat.parent.id)
          : null;

    const parentNode = parentId != null ? categoryMap.get(parentId) : null;

    if (parentNode && String(parentNode.id) !== String(cat.id)) {
      if (!parentNode.children.some((child) => String(child.id) === String(cat.id))) {
        parentNode.children.push(cat);
      }
    } else {
      rootCategories.push(cat);
    }
  });

  return rootCategories;
};

/**
 * Recursively filters categories tree by search term while maintaining tree structure and order.
 *
 * @param {Array} categories - Hierarchical category tree
 * @param {string} searchTerm - Term to search for
 * @returns {Array} - Filtered categories tree
 */
export const filterCategories = (categories, searchTerm) => {
  if (!searchTerm) return categories;
  const term = searchTerm.toLowerCase();
  const filterRecursive = (cats) =>
    cats.reduce((acc, cat) => {
      const matches = (cat.name || "").toLowerCase().includes(term);
      const childMatches = filterRecursive(cat.children || []);
      if (matches || childMatches.length > 0) {
        acc.push({ ...cat, children: childMatches });
      }
      return acc;
    }, []);
  return filterRecursive(categories);
};
